<template>
  <v-card class="pa-1">
    <v-card-title class="d-flex justify-space-between mb-4">
      <span>{{
        !linkModalOpen ? $t("storage.new_link") : $t("storage.link_details")
      }}</span>
      <v-btn icon color="red" @click="handleCloseDialog">
        <v-icon size="20">mdi-close</v-icon>
      </v-btn>
    </v-card-title>
    <v-card-text>
      <label v-if="!linkModalOpen" for="upload-folder-modal__input-name">
        <input
          id="upload-folder-modal__input-name"
          :placeholder="$t('storage.link_name_placeholder')"
          type="text"
          ref="uploadLinkInputRef"
          class="upload-folder-modal__input"
          v-model.trim="name"
          @keyup.enter="saveLink"
        />
        <p class="error-label">{{ nameError }}</p>
      </label>
      <p v-else>{{ renderHyperLinkName }}</p>
      <label v-if="!linkModalOpen" for="upload-folder-modal__input-hyper-link">
        <input
          id="upload-folder-modal__input-hyper-link"
          :placeholder="$t('storage.link_url_placeholder')"
          type="text"
          class="upload-folder-modal__input"
          v-model.trim="hyperLink"
          @keyup.enter="saveLink"
        />
        <p class="error-label">{{ hyperLinkError }}</p>
      </label>

      <a
        :href="renderHyperLink"
        target="_blank"
        v-else
        class="primary--text text-decoration-underline"
      >
        {{ renderHyperLink }}
      </a>
    </v-card-text>
    <v-card-actions v-if="!linkModalOpen" class="d-flex justify-center">
      <CommonButton
        color="secondary"
        outlined
        :label="$t('common.cancel')"
        class="px-4"
        width="100"
        @handleClick="handleCloseDialog"
      />
      <CommonButton
        :label="$t('common.save')"
        class="px-4"
        width="100"
        @handleClick="saveLink"
      />
    </v-card-actions>
  </v-card>
</template>

<script>
import CommonButton from "@/views/academy/common/CommonButton";
import { mapActions, mapGetters, mapMutations } from "vuex";
import { ModuleTypes } from "@/store/ModuleTypes";
import { StorageItemTypes } from "@/views/academy/enums/StorageItemTypes";
import { RegExps } from "@/views/academy/enums/RegExps";
import { i18n } from "@/plugins/i18n";

export default {
  name: "UploadLinkModal",
  components: {
    CommonButton
  },
  props: {
    closeModal: {
      type: Function,
      required: true
    }
  },

  data() {
    return {
      name: "",
      hyperLink: "",
      nameError: "",
      hyperLinkError: ""
    };
  },

  computed: {
    ...mapGetters(ModuleTypes.STORAGE, [
      "activeOrganization",
      "isAddFolderDialogOpened",
      "storageContent",
      "linkModalOpen",
      "storageItemDetails"
    ]),
    ...mapGetters(ModuleTypes.AUTH, ["userRoles"]),

    renderHyperLink() {
      const hyperLink = this.storageItemDetails?.hyperLink;
      const protocolSuffix = "://";

      if (
        hyperLink?.includes(`http${protocolSuffix}`) ||
        hyperLink?.includes(`https${protocolSuffix}`)
      ) {
        return hyperLink;
      } else {
        return `http://${hyperLink}`;
      }
    },
    renderHyperLinkName() {
      return this.storageItemDetails?.name;
    }
  },

  mounted() {
    setTimeout(
      () => this.$nextTick(() => this.$refs.uploadLinkInputRef?.focus()),
      200
    );
  },

  watch: {
    isAddFolderDialogOpened(value) {
      if (value) {
        setTimeout(() => this.$refs.uploadLinkInputRef?.focus(), 200);
      }
    }
  },

  methods: {
    ...mapActions(ModuleTypes.ACADEMY_NAVIGATOR, ["getOrganizations"]),
    ...mapActions(ModuleTypes.STORAGE, [
      "addStorageItem",
      "fetchStorageContent"
    ]),
    ...mapMutations(ModuleTypes.STORAGE, [
      "toggleAddFolderDialog",
      "toggleStorageLinkModal"
    ]),

    handleCloseDialog() {
      this.name = "";
      this.closeModal && this.closeModal();
      this.toggleStorageLinkModal({ linkModalOpen: false });
    },

    validateLink() {
      const validateFields = [
        {
          value: this.name,
          errorField: "nameError",
          error: () => {
            if (!this.name) {
              return i18n.tc("validation.min_1");
            } else if (this.name?.trim()?.length > 255) {
              return i18n.tc("validation.max_255");
            }
          }
        },
        {
          value: this.hyperLink,
          errorField: "hyperLinkError",
          error: () => {
            if (!this.hyperLink) {
              return i18n.tc("validation.field_is_required");
            } else if (!this.hyperLink.match(RegExps.URL)) {
              return i18n.tc("validation.url_invalid_format");
            }
          }
        }
      ];
      let isInvalid = true;
      const errors = validateFields.map(item => {
        isInvalid = item.error();
        return {
          error: isInvalid
            ? (this[item.errorField] = isInvalid)
            : (this[item.errorField] = "")
        };
      });
      return errors.every(item => !item.error);
    },

    saveLink() {
      if (this.validateLink()) {
        const breadcrumbs = this.storageContent?.breadcrumbs;
        this.addStorageItem({
          name: this.name,
          hyperLink: this.hyperLink,
          organizationId: this.$route.params.orgId,
          type: StorageItemTypes.LINK,
          parentId: breadcrumbs[breadcrumbs?.length - 1]?.id || null,
          sharedWithMe: breadcrumbs[breadcrumbs?.length - 1]?.sharedWithMe
        });
        this.handleCloseDialog();
      }
    }
  }
};
</script>

<style scoped>
.v-text-field .v-input__control .v-input__slot {
  min-height: auto !important;
  display: flex !important;
  align-items: center !important;
}

.v-input__slot fieldset {
  height: 45px !important;
  font-size: 0.5rem;
}

.upload-folder-modal__input {
  width: 100%;
  padding: 8px;
  font-size: 1rem;
  border: 2px solid var(--grey-color);
  border-radius: 5px;
  outline: none;
}

.upload-folder-modal__input:focus {
  border: 2px solid var(--primary-color);
}
.error-label {
  margin-bottom: 0;
  min-height: 22px;
  padding-left: 10px;
  font-size: 12px;
  color: #f00;
}
</style>
